import { useEffect, useState, useRef } from 'react';
import { dashboardService } from '../../../services';
import NewsCard from './NewsCard';
import UserNewsStats from './UserNewsStats';
import './Dashboard.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    // Prevent duplicate calls when a fetch is already in progress
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    try {
      setLoading(true);
      const response = await dashboardService.getDashboardStats();
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      fetchInProgressRef.current = false;
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const formatStatus = (status) => {
    const statusMap = {
      draft: 'Draft',
      in_review: 'In Review',
      published: 'Published',
      archived: 'Archived',
      rejected: 'Rejected',
    };
    return statusMap[status?.toLowerCase()] || status || 'Draft';
  };

  const getRootDomain = (domain) => {
    if (!domain) return domain;
    const parts = domain.split('.');
    // If domain has more than 2 parts, extract root domain (last 2 parts)
    // Handle common ccTLDs like co.uk, com.au, etc.
    const commonSecondLevelDomains = ['co', 'com', 'org', 'net', 'gov', 'edu', 'ac'];
    if (parts.length > 2) {
      // Check if the second-level part is a common SLD for ccTLDs
      if (commonSecondLevelDomains.includes(parts[parts.length - 2])) {
        return parts.slice(-3).join('.');
      }
      return parts.slice(-2).join('.');
    }
    return domain;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="dashboard-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard-error">
          <p>{error}</p>
          <button onClick={fetchDashboardStats} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  const { uniqueDomains, recentNews } = dashboardData;

  return (
    <div className="dashboard">
      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Per-user news creation / go-live / edit stats */}
        <UserNewsStats />

        {/* Recent News */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Recent Published News</h2>
            <span className="card-badge">{recentNews.length}</span>
          </div>
          <div className="card-content">
            {recentNews.length === 0 ? (
              <div className="empty-state">
                <p>No published news yet</p>
              </div>
            ) : (
              <div className="news-cards-list">
                {recentNews.map((news) => (
                  <NewsCard
                    key={news._id}
                    news={news}
                    showEdit={false}
                    showAddToDeckButton={false}
                    showPublishButton={true}
                    onGoLiveSuccess={() => fetchDashboardStats()}
                  />
                ))}
                {/* Show dummy card on desktop if odd number of news items */}
                {recentNews.length % 2 === 1 && (
                  <div className="dummy-news-card">
                    No more news to show
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* News Sources - full width, below the grid */}
      <div className="dashboard-card dashboard-card-full">
        <div className="card-header">
          <h2 className="card-title">News Sources</h2>
          <span className="card-badge">{uniqueDomains.length}</span>
        </div>
        <div className="card-content">
          {uniqueDomains.length === 0 ? (
            <div className="empty-state">
              <p>No source domains yet</p>
            </div>
          ) : (
            <div className="domains-chips">
              {uniqueDomains.map((domain, index) => (
                <a
                  key={index}
                  href={`https://${domain.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="domain-chip"
                  title={`${domain.domain} (${domain.count} articles)`}
                >
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${getRootDomain(domain.domain)}&sz=32`}
                    alt={domain.domain}
                    className="domain-chip-icon"
                    onError={(e) => {
                      e.target.src = '/assets/icons/globe.svg';
                    }}
                  />
                  <span className="domain-chip-name">{domain.domain}</span>
                  <span className="domain-chip-count">{domain.count}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
